import React, { useEffect, useMemo, useState } from "react";
import { RiWhatsappFill } from "react-icons/ri";
import { Link, useParams } from "react-router-dom";
import DOMPurify from "dompurify";

import { getWhatsAppHref } from "../config/contactChannels";
import { useApi } from "../contexts/ApiProvider";
import { useFlash } from "../contexts/FlashProvider";
import {
  fallbackServices,
  getServiceImageUrl,
  normalizeService,
} from "../helpers/serviceFallbacks";

const fallbackSteps = [
  "Analyse du besoin et cadrage de la demande",
  "Proposition d'une solution adaptee au contexte",
  "Mise en oeuvre avec suivi et ajustements",
];

function findFallbackService(slug) {
  return fallbackServices.find((service) => service.slug === slug);
}

function formatDate(date) {
  if (!date) return null;

  return new Intl.DateTimeFormat("fr-FR", {
    month: "long",
    year: "numeric",
  }).format(new Date(date));
}

function getImageGallery(service) {
  if (service?.images?.length) {
    return service.images.map((image, index) =>
      getServiceImageUrl({
        ...service,
        images: [image],
        imageName: image.name || service.imageName,
      })
    );
  }

  return [getServiceImageUrl(service)];
}

export default function ServicePage() {
  const api = useApi();
  const flash = useFlash();
  const { slug } = useParams();
  const [service, setService] = useState();
  const [relatedServices, setRelatedServices] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let isMounted = true;

    (async () => {
      setIsLoading(true);
      setNotFound(false);

      const localFallback = findFallbackService(slug);
      const response = await api.get(`/api/services/slug/${slug}`);

      if (!isMounted) return;

      if (response.success && response?.service) {
        const normalized = normalizeService(response.service);
        setService(normalized);

        const relatedResponse = await api.get("/api/services/search");
        if (!isMounted) return;

        if (relatedResponse.success && relatedResponse?.services?.length) {
          const related = relatedResponse.services
            .map(normalizeService)
            .filter((item) => item.slug !== normalized.slug)
            .filter((item) =>
              item.tags?.some((tag) => normalized.tags?.includes(tag))
            )
            .slice(0, 3);
          setRelatedServices(related);
        }
      } else if (localFallback) {
        setService(localFallback);
        setRelatedServices(
          fallbackServices
            .filter((item) => item.slug !== localFallback.slug)
            .slice(0, 3)
        );
      } else {
        setNotFound(true);
        flash(response.error?.message || "Service introuvable", "danger");
      }

      setIsLoading(false);
    })();

    return () => {
      isMounted = false;
    };
  }, [api, flash, slug]);

  const gallery = useMemo(() => (service ? getImageGallery(service) : []), [service]);
  const sanitizedContent = useMemo(
    () => DOMPurify.sanitize(service?.content || ""),
    [service?.content]
  );
  const publishedAt = formatDate(service?.createdAt);
  const likesCount = service?.likes?.length || 0;
  const commentsCount = service?.comments?.length || 0;
  const whatsappHref = getWhatsAppHref(
    `Bonjour Fatihoune, je souhaite avoir des informations sur le service: ${
      service?.title || "Service Fatihoune"
    }.`
  );

  if (isLoading) {
    return (
      <main className="service-detail-page">
        <section className="container py-10 text-center">
          <span className="spinner-border text-success" role="status" />
          <p className="text-muted mt-3 mb-0">Chargement du service...</p>
        </section>
      </main>
    );
  }

  if (notFound || !service) {
    return (
      <main className="service-detail-page">
        <section className="container py-10 text-center">
          <p className="section-kicker mb-3">Service introuvable</p>
          <h1 className="display-5 fw-bold mb-4">
            Ce service n'est pas disponible.
          </h1>
          <Link to="/services" className="btn btn-success">
            Revenir aux services
          </Link>
        </section>
      </main>
    );
  }

  return (
    <main className="service-detail-page">
      <section className="service-detail-hero-v2">
        <div className="container">
          <div className="service-detail-hero-grid">
            <div className="service-detail-copy">
              <Link to="/services" className="service-detail-back">
                <span aria-hidden="true">&larr;</span> Tous les services
              </Link>
              <p className="section-kicker mb-3">Service Fatihoune</p>
              <h1>{service.title}</h1>
              <p className="service-detail-subtitle">
                {service.subtitle || service.description}
              </p>

              <div className="service-detail-tags">
                {service.tags.map((tag) => (
                  <span className="service-tag" key={tag}>
                    {tag}
                  </span>
                ))}
              </div>

              <div className="service-detail-actions">
                <a
                  href={whatsappHref}
                  className="btn btn-whatsapp whatsapp-icon-link"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Contacter Fatihoune sur WhatsApp"
                  title="WhatsApp"
                >
                  <RiWhatsappFill aria-hidden="true" />
                  <span className="visually-hidden">WhatsApp</span>
                </a>
                <Link to="/contact" className="btn btn-success">
                  Demander des informations
                </Link>
                {service.url && (
                  <a
                    href={service.url}
                    className="btn btn-outline-dark"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Lien externe
                  </a>
                )}
              </div>
            </div>

            <div className="service-detail-gallery">
              <img src={gallery[0]} alt={service.title} />
              {gallery.length > 1 && (
                <div className="service-detail-thumbs">
                  {gallery.slice(1, 4).map((imageUrl, index) => (
                    <img
                      src={imageUrl}
                      alt={`${service.title} ${index + 2}`}
                      key={imageUrl}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <section className="service-detail-meta-section">
        <div className="container">
          <div className="service-detail-meta-grid">
            <div>
              <span>Vues</span>
              <strong>{service.views || 0}</strong>
            </div>
            <div>
              <span>Interets</span>
              <strong>{likesCount}</strong>
            </div>
            <div>
              <span>Commentaires</span>
              <strong>{commentsCount}</strong>
            </div>
            <div>
              <span>Mise à jour</span>
              <strong>{publishedAt || "Disponible"}</strong>
            </div>
          </div>
        </div>
      </section>

      <section className="service-detail-body-section">
        <div className="container">
          <div className="service-detail-body-grid">
            <article className="service-detail-content-panel">
              <p className="section-kicker mb-3">Presentation</p>
              <h2>{service.subtitle || "Un accompagnement structure"}</h2>
              <p className="lead text-muted">{service.description}</p>

              {sanitizedContent.trim() ? (
                <div
                  className="description service-detail-richtext"
                  dangerouslySetInnerHTML={{ __html: sanitizedContent }}
                />
              ) : (
                <div className="service-detail-empty-content">
                  <p>
                    Les informations detaillees de ce service seront ajoutees
                    depuis l'interface admin.
                  </p>
                </div>
              )}
            </article>

            <aside className="service-detail-side">
              <div className="service-detail-side-card">
                <p className="section-kicker mb-3">Methode</p>
                <h3>Comment nous avançons</h3>
                <ol>
                  {fallbackSteps.map((step) => (
                    <li key={step}>{step}</li>
                  ))}
                </ol>
              </div>

              <div className="service-detail-side-card service-detail-cta-card">
                <p className="section-kicker mb-3">Besoin d'aide ?</p>
                <h3>Parlez-nous de votre besoin.</h3>
                <p>
                  L'équipe Fatihoune vous aide à choisir le bon service et les
                  prochaines étapes.
                </p>
                <a
                  href={whatsappHref}
                  className="btn btn-whatsapp whatsapp-icon-link w-100 mb-2"
                  target="_blank"
                  rel="noreferrer"
                  aria-label="Contacter Fatihoune sur WhatsApp"
                  title="WhatsApp"
                >
                  <RiWhatsappFill aria-hidden="true" />
                  <span className="visually-hidden">WhatsApp</span>
                </a>
                <Link to="/contact" className="btn btn-success w-100">
                  Remplir le formulaire
                </Link>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {relatedServices.length > 0 && (
        <section className="service-related-section">
          <div className="container">
            <div className="d-flex flex-column flex-lg-row justify-content-between gap-3 mb-4">
              <div>
                <p className="section-kicker mb-2">Services lies</p>
                <h2 className="h1 fw-bold mb-0">A explorer ensuite</h2>
              </div>
              <Link to="/services" className="btn btn-outline-dark align-self-lg-end">
                Tous les services
              </Link>
            </div>

            <div className="row g-4">
              {relatedServices.map((related) => (
                <div className="col-12 col-md-4" key={related._id || related.slug}>
                  <article className="service-related-card">
                    <img src={getServiceImageUrl(related)} alt={related.title} />
                    <div>
                      <h3>{related.title}</h3>
                      <p>{related.description}</p>
                      <Link to={`/services/${related.slug}`}>
                        Consulter <span aria-hidden="true">&rarr;</span>
                      </Link>
                    </div>
                  </article>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </main>
  );
}
